import { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import useUserSession from './useUserSession';
import { useMainContext } from '../hooks/useMainContext';
import { setAuthInstance } from '../utils/getIdToken';

/**
 * Enhanced authentication hook that handles both Cognito and OIDC (Google) authentication
 * This provides a unified interface for authentication state management
 */
export default function useEnhancedAuth() {
    const [isLoading, setIsLoading] = useState(false);
    const auth = useAuth();
    const { checkAuth: checkCognitoAuth } = useUserSession();
    const { viewManager } = useMainContext();
    useEffect(() => {
        setAuthInstance(auth);
    }, [auth]);
    // Check authentication status on mount and when auth state changes
    useEffect(() => {
        const checkAuthenticationState = async () => {
            setIsLoading(true);
            try {
                // First check if user is authenticated via OIDC (Google)
                if (auth.isAuthenticated && auth.user) {
                    const user = auth.user?.profile;
                    const userData = {
                        name:
                            user.name ||
                            `${user.given_name || ''} ${user.family_name || ''}`.trim(),
                        firstName:
                            user.given_name || user.name?.split(' ')[0] || '',
                        lastName:
                            user.family_name || user.name?.split(' ')[1] || '',
                        email: user.email,
                        id: user.sub,
                        authProvider: 'google',
                    };

                    viewManager.setUserData(userData);
                    viewManager.setIsLoggedIn(true);
                    return;
                } // If not authenticated via OIDC, check Cognito
                try {
                    await checkCognitoAuth();
                } catch (cognitoError) {
                    console.warn('Cognito auth check failed:', cognitoError);
                    // If both OIDC and Cognito fail, ensure user is logged out
                    viewManager.setIsLoggedIn(false);
                    viewManager.setUserData(null);
                }
            } catch (error) {
                console.error('Error checking authentication state:', error);
                viewManager.setIsLoggedIn(false);
                viewManager.setUserData(null);
            } finally {
                setIsLoading(false);
            }
        };

        if (!auth.isLoading) {
            checkAuthenticationState();
        }
    }, [
        auth.isAuthenticated,
        auth.user,
        auth.isLoading,
        checkCognitoAuth,
        viewManager,
    ]);

    const isAuthenticated = viewManager.isLoggedIn;
    const user = viewManager.userData;
    const authLoading = isLoading || auth.isLoading;

    return {
        isAuthenticated,
        user,
        isLoading: authLoading,
        authProvider: user?.authProvider || 'cognito',
    };
}
